/**
 * @description Hook utilizado para manipular os exames necroscópicos
 * @author Diego Sabino Amorim de Araújo
 * @file useExameNecroscopicoService.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import { useDispatch } from 'react-redux'
import actionTypes from 'utils/actionTypes'
import { useArquivoService } from './useArquivoService'
import { useFetch } from './useFetch'

export const useExameNecroscopicoService = () => {
  const fetch = useFetch()
  const dispatch = useDispatch()
  const arquivoService = useArquivoService()

  const listarExameNecroscopicoDadosFormulario = async (id, setExameNecroscopico, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/exameNecroscopico/${id}/dadosFormulario`)
      setExameNecroscopico(response.data.exameNecroscopico)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const editarExameNecroscopico = async (id, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        ...values,
        exSubs: null,
      }
      await fetch.put(`exameNecroscopico/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const editarConclusao = async (id, values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const dados = {
        ...values,
        id_causa_morte: values.causaMorte ? values.causaMorte.id : null,
        exSubs: values.exSubs ? values.exSubs.map((ex) => ex.id) : null,
      }
      await fetch.put(`exameNecroscopico/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTiposFenomenosCadavericosGlobal = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/exameNecroscopico/tiposFenomenosCadavericos`)
      dispatch({
        type: actionTypes.TIPOS_FENOMENOS_CADAVERICOS,
        opcoes: response.data.tiposFenomenosCadavericos,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarTiposLesoesGlobal = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/exameNecroscopico/tiposLesao`)
      dispatch({
        type: actionTypes.TIPOS_LESOES,
        opcoes: response.data.tiposLesao,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCausasMorteGlobal = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`exameNecroscopico/causasMorte`)
      dispatch({
        type: actionTypes.CAUSAS_MORTE,
        opcoes: response.data.causasMorte,
      })
      return response.data.causasMorte
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const setarSVGS = async (idExame, valuesTatuagem, valuesLesoes, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const responseSVGTatuagemFront = await arquivoService.uploadSVGCalunga(valuesTatuagem.svgFront, 'svg_tatuagem_front')
      const responseSVGTatuagemBack = await arquivoService.uploadSVGCalunga(valuesTatuagem.svgBack, 'svg_tatuagem_back')
      const responseSVGTatuagemLeft = await arquivoService.uploadSVGCalunga(valuesTatuagem.svgLeft, 'svg_tatuagem_left')
      const responseSVGTatuagemRight = await arquivoService.uploadSVGCalunga(valuesTatuagem.svgRight, 'svg_tatuagem_right')
      const responseSVGLesaoFront = await arquivoService.uploadSVGCalunga(valuesLesoes.svgFront, 'svg_lesao_front')
      const responseSVGLesaoBack = await arquivoService.uploadSVGCalunga(valuesLesoes.svgBack, 'svg_lesao_back')
      const responseSVGLesaoLeft = await arquivoService.uploadSVGCalunga(valuesLesoes.svgLeft, 'svg_lesao_left')
      const responseSVGLesaoRight = await arquivoService.uploadSVGCalunga(valuesLesoes.svgRight, 'svg_lesao_right')

      const dados = {
        id_svg_tatuagem_front: responseSVGTatuagemFront.data.location,
        id_svg_tatuagem_back: responseSVGTatuagemBack.data.location,
        id_svg_tatuagem_left: responseSVGTatuagemLeft.data.location,
        id_svg_tatuagem_right: responseSVGTatuagemRight.data.location,
        id_svg_lesao_front: responseSVGLesaoFront.data.location,
        id_svg_lesao_back: responseSVGLesaoBack.data.location,
        id_svg_lesao_left: responseSVGLesaoLeft.data.location,
        id_svg_lesao_right: responseSVGLesaoRight.data.location,
      }

      await fetch.put(`exameNecroscopico/${idExame}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  return {
    listarExameNecroscopicoDadosFormulario,
    editarExameNecroscopico,
    editarConclusao,
    listarTiposFenomenosCadavericosGlobal,
    listarTiposLesoesGlobal,
    listarCausasMorteGlobal,
    setarSVGS,
  }
}
